import { ActiveContractLabels } from '../../../constants/strings/billing-label-enums';
import {
  ContractPackagesType,
  ContractTemplateType,
  ContractType,
} from '../../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../../utils/bills-utils';
import { formatCurrency } from '../../../utils/currency-helpers';
import { formatDate } from '../../../utils/date-helper';
import { IDiscount } from '../../../utils/discount-utils';
import { ValidBillingModel } from '../package/custom-package-types';

export interface PackageDisplayContext {
  contractPackage: ContractPackagesType;
  contract: ContractType;
  packageKey: string;
  templates: ContractTemplateType[];
  discounts?: IDiscount[];
  productOfferingIds?: Set<number>;
  slimDisplay: boolean;
}

export interface TextFieldConfig {
  kind: 'text';
  label: string;
  getValue: (ctx: PackageDisplayContext) => string | null;
}

export interface FieldGroupConfig {
  kind: 'group';
  fields: TextFieldConfig[];
}

export interface SectionConfig {
  kind: 'section';
  sectionId:
    | 'billableActivities'
    | 'discounts'
    | 'milestones'
    | 'costShareOptions';
  hasDivider?: boolean;
}

export type GridItem = TextFieldConfig | FieldGroupConfig | SectionConfig;

export interface BillingModelDisplayConfig {
  gridItems: GridItem[];
  belowGridSections: SectionConfig[];
}

// -- Shared getValue helpers --

const billingModelName = (ctx: PackageDisplayContext): string =>
  capitalizeFirstLetterAndSpaceSnakeString(
    ctx.contractPackage.billingModel.name,
  );

const billingTypeOverride = (ctx: PackageDisplayContext): string =>
  capitalizeFirstLetterAndSpaceSnakeString(
    ctx.contractPackage.billingTypeOverride ?? 'None',
  );

const price = (ctx: PackageDisplayContext): string =>
  formatCurrency(ctx.contractPackage.price, ctx.contract.currency);

const startDate = (ctx: PackageDisplayContext): string =>
  formatDate(ctx.contractPackage.startDate ?? ctx.contract.startDate);

const templateName = (ctx: PackageDisplayContext): string =>
  ctx.templates.find(t => t.id === ctx.contractPackage.contractTemplateId)
    ?.name ?? 'Template not found';

// -- Reusable field definitions --

const billingModelField: TextFieldConfig = {
  kind: 'text',
  label: 'Billing Model',
  getValue: billingModelName,
};

const billingTypeOverrideField: TextFieldConfig = {
  kind: 'text',
  label: 'Billing Type Override',
  getValue: billingTypeOverride,
};

const priceField: TextFieldConfig = {
  kind: 'text',
  label: 'Price',
  getValue: price,
};

const startDateField: TextFieldConfig = {
  kind: 'text',
  label: 'Start Date',
  getValue: startDate,
};

const billingInfoFields: TextFieldConfig[] = [
  billingModelField,
  billingTypeOverrideField,
  startDateField,
];

const billingInfoGroup: FieldGroupConfig = {
  kind: 'group',
  fields: billingInfoFields,
};

// -- Milestone config (shared by individual and cohort) --

const milestoneConfig: BillingModelDisplayConfig = {
  gridItems: [
    billingInfoGroup,
    { kind: 'text', label: 'Template', getValue: templateName },
    priceField,
    { kind: 'section', sectionId: 'discounts' },
  ],
  belowGridSections: [
    { kind: 'section', sectionId: 'milestones', hasDivider: true },
  ],
};

// -- Config map --

export const BILLING_MODEL_DISPLAY_CONFIG: Record<
  ValidBillingModel,
  BillingModelDisplayConfig
> = {
  [ValidBillingModel.ENGAGEMENT]: {
    gridItems: [
      {
        kind: 'group',
        fields: [...billingInfoFields, priceField],
      },
      {
        kind: 'group',
        fields: [
          {
            kind: 'text',
            label: ActiveContractLabels.ENGAGEMENT_INITIAL_FEE,
            getValue: ctx =>
              formatCurrency(
                ctx.contractPackage.rules?.activationFee ?? 0,
                ctx.contract.currency,
              ),
          },
          {
            kind: 'text',
            label: ActiveContractLabels.ENGAGEMENT_ACTIVITY_FEE,
            getValue: ctx =>
              formatCurrency(
                ctx.contractPackage.rules?.activityFee ?? 0,
                ctx.contract.currency,
              ),
          },
        ],
      },
      { kind: 'section', sectionId: 'billableActivities' },
      { kind: 'section', sectionId: 'discounts' },
    ],
    belowGridSections: [],
  },

  [ValidBillingModel.STANDARD]: {
    gridItems: [
      billingInfoGroup,
      priceField,
      { kind: 'section', sectionId: 'billableActivities' },
      { kind: 'section', sectionId: 'discounts' },
    ],
    belowGridSections: [],
  },

  [ValidBillingModel.INDIVIDUAL_MILESTONE]: milestoneConfig,
  [ValidBillingModel.COHORT_MILESTONE]: milestoneConfig,

  [ValidBillingModel.FEE_FOR_SERVICE]: {
    gridItems: [
      billingInfoGroup,
      priceField,
      { kind: 'section', sectionId: 'billableActivities' },
    ],
    belowGridSections: [{ kind: 'section', sectionId: 'costShareOptions' }],
  },

  [ValidBillingModel.ADMIN_FEE_CLAIMS]: {
    gridItems: [
      billingInfoGroup,
      {
        kind: 'text',
        label: 'Fee Percent',
        getValue: ctx => `${ctx.contractPackage.rules?.feePercent ?? 0}%`,
      },
      priceField,
      { kind: 'section', sectionId: 'discounts' },
    ],
    belowGridSections: [],
  },
};
